import { watch_entity_reference_inputs } from './lib/entity-picker'
import { PRODUCT } from './lib/platform'
import { apply_seo, seo_for_path } from './lib/seo'
import { get_session_user } from './lib/session'
import {
  destroy_workspace,
  ensure_workspace_and_open,
  is_workspace_active,
  is_workspace_path,
  normalize_path,
  register_workspace_loaders
} from './lib/workspace'

export interface PageModule {
  mount(el: HTMLElement): void | Promise<void>
  unmount?(): void
}

export interface Route {
  path: string
  loader: () => Promise<PageModule>
}

class Router {
  private routes: Route[] = []
  private current_module: PageModule | null = null
  private container: HTMLElement | null = null
  private stop_entity_reference_watch: (() => void) | null = null
  private workspace_registered = false

  register(path: string, loader: Route['loader']): void {
    this.routes.push({ path, loader })
  }

  private ensure_workspace_loaders(): void {
    if (this.workspace_registered) return
    const entries = this.routes
      .filter(r => is_workspace_path(r.path))
      .map(r => ({
        path: r.path,
        // Dashboard autenticado usa home, não landing
        loader: r.path === '/'
          ? () => import('./pages/home')
          : r.loader
      }))
    register_workspace_loaders(entries)
    this.workspace_registered = true
  }

  setup(container: HTMLElement): void {
    this.container = container
    this.ensure_workspace_loaders()

    window.addEventListener('popstate', () => {
      void this.navigate(window.location.pathname, false)
    })

    document.addEventListener('click', (e) => {
      const anchor = (e.target as HTMLElement).closest('a') as HTMLAnchorElement | null
      if (!anchor) return
      const href = anchor.getAttribute('href')
      if (!href || href.startsWith('#') || href.startsWith('http') || href.startsWith('//')) return
      // Rotas da API (OAuth, export CSV, docs) exigem navegação full-page — não interceptar.
      if (href.startsWith('/api/')) return
      if (anchor.hasAttribute('target') || anchor.hasAttribute('download')) return
      if (e.ctrlKey || e.metaKey || e.shiftKey) return
      e.preventDefault()
      void this.navigate(href)
    })
  }

  private async mount_standalone(path: string, route: Route): Promise<void> {
    if (!this.container) throw new Error('Router not initialized: missing container')

    if (is_workspace_active()) {
      destroy_workspace()
    }

    if (this.stop_entity_reference_watch) {
      this.stop_entity_reference_watch()
      this.stop_entity_reference_watch = null
    }

    if (this.current_module?.unmount) this.current_module.unmount()
    this.container.innerHTML = ''

    const mod = await route.loader()
    this.current_module = mod
    await mod.mount(this.container)
    this.stop_entity_reference_watch = watch_entity_reference_inputs(this.container)
  }

  private render_not_found(): void {
    if (!this.container) return
    if (is_workspace_active()) destroy_workspace()
    if (this.current_module?.unmount) this.current_module.unmount()
    this.current_module = null
    this.container.innerHTML = `
      <div class="mx-auto max-w-3xl p-8">
        <div class="rounded-sm border border-border bg-surface p-6">
          <h1 class="text-2xl font-semibold tracking-tight text-ink">404</h1>
          <p class="mt-2 text-sm text-muted">Rota não encontrada.</p>
          <a class="mt-4 inline-flex items-center justify-center rounded-sm bg-primary px-4 py-2 text-sm font-semibold text-primary-ink hover:bg-primary/90" href="/">Voltar</a>
        </div>
      </div>
    `
  }

  async navigate(path: string, push = true): Promise<void> {
    if (!this.container) throw new Error('Router not initialized: missing container')

    const clean = normalize_path(path.split('?')[0].split('#')[0])
    if (push && window.location.pathname !== clean) {
      window.history.pushState({}, '', clean)
    }

    apply_seo(seo_for_path(clean))
    const route = this.routes.find(r => r.path === clean) || null

    // Login sempre standalone
    if (clean === '/login') {
      if (!route) {
        this.render_not_found()
        return
      }
      await this.mount_standalone(clean, route)
      return
    }

    // Workspace paths: se autenticado, abre em aba
    if (is_workspace_path(clean)) {
      const user = await get_session_user()
      if (user) {
        // Limpa módulo standalone residual
        if (this.current_module?.unmount) {
          this.current_module.unmount()
          this.current_module = null
        }
        const prev_watch = this.stop_entity_reference_watch
        this.stop_entity_reference_watch = null
        if (prev_watch) prev_watch()

        this.ensure_workspace_loaders()
        await ensure_workspace_and_open(clean, {
          root: this.container,
          user_id: String(user.id),
          push: false // URL já foi pushState acima se necessário
        })

        // Entity picker no shell (outlet + painéis)
        this.stop_entity_reference_watch = watch_entity_reference_inputs(this.container)
        return
      }

      // Convidado: só `/` vira landing (produto com landing pública); demais
      // paths → login. No SGQ o host é de app: convidado em `/` também vai
      // pro login (a landing comercial vive no apex isgq.com.br).
      if (clean !== '/' || !PRODUCT.landing_guest) {
        if (push || window.location.pathname !== '/login') {
          const suffix = clean !== '/' ? `?redirect=${encodeURIComponent(clean)}` : ''
          window.history.pushState({}, '', `/login${suffix}`)
        }
        const login_route = this.routes.find(r => r.path === '/login')
        if (login_route) {
          // O SEO foi aplicado para o path original; o mount real é /login.
          apply_seo(seo_for_path('/login'))
          await this.mount_standalone('/login', login_route)
          return
        }
      }
    }

    if (!route) {
      this.render_not_found()
      return
    }

    await this.mount_standalone(clean, route)
  }
}

export const router = new Router()

router.register('/', () => import('./pages/landing'))
router.register('/login', () => import('./pages/login'))
router.register('/modules', () => import('./pages/modules'))
router.register('/conformidade', () => import('./pages/conformidade'))
router.register('/gap-analysis', () => import('./pages/gap-analysis'))
router.register('/auditorias', () => import('./pages/auditorias'))
router.register('/nao-conformidades', () => import('./pages/nao-conformidades'))
router.register('/modules/m01', () => import('./pages/modules/m01'))
router.register('/modules/m02', () => import('./pages/modules/m02'))
router.register('/modules/m03', () => import('./pages/modules/m03'))
router.register('/modules/m04', () => import('./pages/modules/m04'))
router.register('/modules/m05', () => import('./pages/modules/m05'))
router.register('/modules/m06', () => import('./pages/modules/m06'))
router.register('/modules/m07', () => import('./pages/modules/m07'))
router.register('/modules/m08', () => import('./pages/modules/m08'))
router.register('/modules/m09', () => import('./pages/modules/m09'))
router.register('/modules/m10', () => import('./pages/modules/m10'))
router.register('/modules/m11', () => import('./pages/modules/m11'))
router.register('/modules/m12', () => import('./pages/modules/m12'))
router.register('/modules/m13', () => import('./pages/modules/m13'))
router.register('/modules/m14', () => import('./pages/modules/m14'))
router.register('/modules/m15', () => import('./pages/modules/m15'))
router.register('/modules/m16', () => import('./pages/modules/m16'))
router.register('/modules/m17', () => import('./pages/modules/m17'))
router.register('/modules/m18', () => import('./pages/modules/m18'))
router.register('/modules/m19', () => import('./pages/modules/m19'))
router.register('/modules/m20', () => import('./pages/modules/m20'))
router.register('/modules/m21', () => import('./pages/modules/m21'))
router.register('/modules/m22', () => import('./pages/modules/m22'))
router.register('/modules/m23', () => import('./pages/modules/m23'))
router.register('/modules/m24', () => import('./pages/modules/m24'))
router.register('/modules/m25', () => import('./pages/modules/m25'))
router.register('/modules/m26', () => import('./pages/modules/m26'))
router.register('/modules/m27', () => import('./pages/modules/m27'))
router.register('/modules/m28', () => import('./pages/modules/m28'))
router.register('/admin/users', () => import('./pages/admin-users'))
router.register('/admin/acl', () => import('./pages/admin-acl'))
router.register('/admin/platform', () => import('./pages/admin-platform'))
router.register('/admin/integrations', () => import('./pages/admin-integrations'))
router.register('/account', () => import('./pages/account'))
router.register('/privacy', () => import('./pages/privacy'))
router.register('/security', () => import('./pages/security'))
router.register('/onboarding', () => import('./pages/onboarding'))
